import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { SITE, SALON_PHOTOS } from "@/data/site";

export default function Hero({ onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="accueil"
      ref={ref}
      className="relative h-screen w-full overflow-hidden"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div
        style={{ y }}
        className="absolute inset-0 -top-10 -bottom-10"
      >
        <img
          src={SALON_PHOTOS.table}
          alt="Salon de massage Harmony Montélimar"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/30 via-stone-900/50 to-stone-900/80" />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-32"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="flex items-center gap-4 mb-8"
        >
          <div className="w-12 h-px bg-[#C5A059]" />
          <span className="text-[#C5A059] text-xs tracking-[0.4em] uppercase font-body">
            Salon de massage · Montélimar
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, delay: 0.6, ease: "easeOut" }}
          className="font-display font-light text-[#F9F6F0] text-shadow-soft text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] max-w-4xl"
          data-testid="hero-title"
        >
          L'art du
          <br />
          <span className="italic font-normal text-[#E6CB89]">lâcher-prise</span>.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1 }}
          className="mt-6 max-w-xl text-[#F9F6F0]/80 text-lg font-light leading-relaxed"
        >
          Un moment de détente et de bien-être absolu, au cœur d'un écrin
          dédié à votre sérénité.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={onBook}
            data-testid="hero-book-btn"
            className="group inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#C5A059] text-white font-body font-medium hover:bg-[#A38044] transition-all duration-500 hover:-translate-y-1 shadow-xl shadow-amber-900/20"
          >
            Réserver maintenant
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
          </button>
          <a
            href="#massages"
            data-testid="hero-scroll-btn"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-[#F9F6F0]/40 text-[#F9F6F0] font-body font-light hover:bg-white/10 transition-all duration-500"
          >
            Découvrir nos soins
          </a>
        </motion.div>
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 8, 0] }}
        transition={{
          opacity: { delay: 1.5, duration: 1 },
          y: { repeat: Infinity, duration: 2, ease: "easeInOut" },
        }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#F9F6F0]/60 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] tracking-[0.4em] uppercase">Scroll</span>
        <ArrowDown strokeWidth={1} className="w-4 h-4" />
      </motion.div>
    </section>
  );
}
